import { RouteRecordRaw } from 'vue-router';
import { ModelType } from '@llws/platform-shared-types/types/db/auth';
import { IRouteConfigAuthMeta, IRouteMetaPermissionConfig, postTypes, ERouteMetaPermissionMode } from '@/shared';
import app from '@/main';

export const getBaseSiteAuth = () => ([{
  key: 'sites',
  keyType: 'site',
  accessType: 'read',
  mode: ERouteMetaPermissionMode.AT_LEAST_ONE,
}] as IRouteMetaPermissionConfig[]);

const auth: IRouteConfigAuthMeta = {
  required: true,
  permissions: getBaseSiteAuth(),
};

const siteSettingsAuth: IRouteConfigAuthMeta = {
  required: true,
  permissions: [{ keyType: ModelType.site, key: 'site_management', accessType: 'read', idParam: 'websiteId' }],
};

const superadminAuth: IRouteConfigAuthMeta = {
  required: true,
  permissions: ['superuser'],
};

const websites: RouteRecordRaw = {
  path: '/websites',
  name: 'Websites',
  component: () => import('@/views/websites/Index.vue'),
  meta: {
    auth,
    title: 'Websites',
    titleKey: 'website.routes.websites',
    icon: 'globe',
    active: false,
    parent: null,
  },
};

const websiteSingle: RouteRecordRaw = {
  path: '/websites/:websiteId/details',
  redirect: (vm) => {
    try {
      // check if the user has `pages` page permission, stay. Or app is not defined, stay.
      if (!app() || app()?.$rsGlobals.$userHasAccess('pages', 'read', `${vm.params.websiteId}`)) {
        return `/websites/${vm.params.websiteId}/pages`;
      }
      if (app()?.$rsGlobals.$userHasAccess('cities', 'read', `${vm.params.websiteId}`)) {
        return `/websites/${vm.params.websiteId}/citypages`;
      }
      if (app()?.$rsGlobals.$userHasAccess('site_management', 'read', `${vm.params.websiteId}`)) {
        return `/websites/${vm.params.websiteId}/global-content`;
      }
      if (app()?.$rsGlobals.$userHasAccess('inquiries', 'read', `${vm.params.websiteId}`)) {
        return `/websites/${vm.params.websiteId}/form-submissions`;
      }
      if (app()?.$rsGlobals.$userHasAccess('form_settings', 'read', `${vm.params.websiteId}`)) {
        return `/websites/${vm.params.websiteId}/forms/settings`;
      }
      if (app()?.$rsGlobals.$userHasAccess('menu_editor', 'read', `${vm.params.websiteId}`)) {
        return `/websites/${vm.params.websiteId}/menu`;
      }

      return `/websites/${vm.params.websiteId}/posts`;
    } catch (e) {
      console.warn(e);
      return `/websites/${vm.params.websiteId}/pages`;
    }
  },
  name: 'WebsiteSingle',
  meta: {
    auth,
    title: 'Website Details',
    titleKey: 'website.routes.websiteDetails',
    icon: 'globe',
    active: false,
    parent: websites,
  },
};

const pages: RouteRecordRaw = {
  path: '/websites/:websiteId/pages',
  name: 'Pages',
  component: () => import('@/views/websites/single/pages/Index.vue'),
  meta: {
    auth: {
      ...auth,
      permissions: [{ keyType: 'site', key: 'pages', accessType: 'read', idParam: 'websiteId' }],
    },
    hidePageTitle: true,
    noPagePadding: true,
    hideFromNav: false,
    title: 'Pages',
    titleKey: 'website.routes.pages',
    icon: 'globe',
    active: false,
    parent: websiteSingle,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
    backButtonTarget: () => ({ isCustom: false, target: websites }),
  },
};

const posts: RouteRecordRaw = {
  path: '/websites/:websiteId/posts',
  name: 'Posts',
  component: () => import('@/views/websites/single/posts/Index.vue'),
  meta: {
    // Base auth already requires at least one site read; keep it so list is visible for read-only users
    auth,
    hideFromNav: false,
    dynamicHideFromNav: () => !postTypes.some((pt) => app()?.$rsGlobals.$userHasAccess({
      keyType: 'site',
      key: pt.value.toLowerCase(),
      accessType: 'read',
      idParam: 'websiteId',
    })),
    title: 'Posts',
    titleKey: 'website.routes.posts',
    icon: 'globe',
    active: false,
    noPagePadding: true,
    parent: websiteSingle,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/details`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
};

const postsByType: RouteRecordRaw = {
  path: '/websites/:websiteId/posts/:postType',
  name: 'PostDetails',
  component: () => import('@/views/websites/single/posts/single/Index.vue'),
  meta: {
    auth: {
      required: true,
      permissions: postTypes.map((post) => ({
        keyType: 'site',
        key: post.value.toLowerCase(),
        accessType: 'read',
        checkAtLeastOneModel: true,
      })) as IRouteMetaPermissionConfig[],
      condition: 'or',
    },
    hidePageTitle: true,
    noPagePadding: true,
    title: 'Post Details',
    titleKey: 'website.routes.postDetails',
    icon: 'globe',
    active: false,
    parent: posts,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/details`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        title: vm?.$ts('website.routes.posts') ?? 'Posts',
        to: `/websites/${vm?.$route.params.websiteId}/posts`,
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
    backButtonTarget: () => ({ target: posts, isCustom: false }),
  },
};

const cityPages: RouteRecordRaw = {
  path: '/websites/:websiteId/citypages',
  name: 'CityPages',
  component: () => import('@/views/websites/single/pages/CityPages.vue'),
  meta: {
    auth: {
      required: true,
      permissions: [{ keyType: 'site', key: 'cities', accessType: 'read', idParam: 'websiteId' }],
    },
    title: 'City Pages',
    titleKey: 'website.routes.cityPages',
    icon: 'city',
    hidePageTitle: true,
    noPagePadding: true,
    parent: websiteSingle,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
};

const menu: RouteRecordRaw = {
  path: '/websites/:websiteId/menu',
  name: 'Menu',
  component: () => import('@/views/websites/single/menu/Index.vue'),
  meta: {
    auth: {
      ...auth,
      permissions: [{ keyType: 'site', key: 'menu_editor', accessType: 'read', idParam: 'websiteId' }],
    },
    hidePageTitle: true,
    noPagePadding: true,
    title: 'Menu',
    titleKey: 'website.routes.menu',
    parent: websiteSingle,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
};

const formSubmissions: RouteRecordRaw = {
  path: '/websites/:websiteId/form-submissions',
  name: 'FormSubmissions',
  component: () => import(/* webpackChunkName: "group-websites" */ '@/views/websites/single/form-submissions/Index.vue'),
  meta: {
    auth: {
      ...auth,
      permissions: [{ keyType: 'site', key: 'inquiries', accessType: 'read', idParam: 'websiteId' }],
    },
    icon: 'mailbox',
    title: 'Submissions',
    titleKey: 'website.routes.submissions',
    hidePageTitle: false,
    noPagePadding: true,
    parent: websiteSingle,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/form-submissions`,
        title: 'Forms',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
};

const inquiriesList: RouteRecordRaw = {
  path: '/websites/:websiteId/inquiries/detailview',
  name: 'InquiriesList',
  component: () => import('@/views/websites/single/inquiries/DetailInquiries.vue'),
  meta: {
    auth: {
      ...auth,
      permissions: [{ keyType: 'site', key: 'inquiries', accessType: 'read', idParam: 'websiteId' }],
    },
    icon: 'mailbox',
    title: 'Inquiry Details',
    titleKey: 'website.routes.inquiryDetails',
    hidePageTitle: true,
    noPagePadding: true,
    hideFromNav: true,
    parent: formSubmissions,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/form-submissions`,
        title: 'Forms',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/form-submissions`,
        title: 'Submissions',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
    backButtonTarget: () => ({ isCustom: false, target: formSubmissions }),
  },
};

const customFormsAlt: RouteRecordRaw = {
  path: '/websites/:websiteId/forms/responses',
  name: 'CustomFormSubmissionsAlt',
  component: () => import(/* webpackChunkName: "group-websites" */ '@/views/websites/single/inquiries/CustomFormsInbox.vue'),
  meta: {
    auth: {
      ...auth,
      permissions: [{ keyType: 'site', key: 'inquiries', accessType: 'read', idParam: 'websiteId' }],
    },
    icon: 'mailbox',
    title: 'Custom Forms',
    titleKey: 'website.routes.customForms',
    noPagePadding: true,
    hideFromNav: true,
  },
};

const customFormsList: RouteRecordRaw = {
  path: '/websites/:websiteId/custom-forms-inbox/detailview/:formName',
  name: 'customFormsList',
  component: () => import('@/views/websites/single/inquiries/CustomFormsDetailView.vue'),
  meta: {
    auth: {
      ...auth,
      permissions: [{ keyType: 'site', key: 'inquiries', accessType: 'read', idParam: 'websiteId' }],
    },
    icon: 'mailbox',
    title: 'Submission Details',
    titleKey: 'website.routes.submissionDetails',
    hidePageTitle: true,
    noPagePadding: true,
    hideFromNav: true,
    parent: formSubmissions,
    backButtonTarget: () => ({ isCustom: false, target: formSubmissions }),
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/form-submissions`,
        title: 'Forms',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/form-submissions`,
        title: 'Submissions',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
};

const customFormsListAlt: RouteRecordRaw = {
  path: '/websites/:websiteId/forms/responses/detailview/:formName',
  name: 'CustomFormsListAlt',
  component: () => import(/* webpackChunkName: "group-websites" */ '@/views/websites/single/inquiries/CustomFormsDetailView.vue'),
  meta: {
    auth: {
      ...auth,
      permissions: [{ keyType: 'site', key: 'inquiries', accessType: 'read', idParam: 'websiteId' }],
    },
    icon: 'mailbox',
    title: 'Custom Forms Inbox',
    titleKey: 'website.routes.customFormsInbox',
    hidePageTitle: true,
    noPagePadding: true,
    hideFromNav: true,
    parent: customFormsAlt,
    backButtonTarget: () => ({ isCustom: false, target: customFormsAlt }),
  },
};

const customFields: RouteRecordRaw = {
  path: '/websites/:websiteId/global-content',
  name: 'WebsiteCustomFields',
  component: () => import('@/views/websites/single/global-content/Index.vue'),
  meta: {
    auth: siteSettingsAuth,
    icon: 'toolbox',
    title: 'Global Content',
    titleKey: 'website.routes.globalContent',
    parent: websiteSingle,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
};

const settings: RouteRecordRaw = {
  path: '/websites/:websiteId/settings',
  name: 'WebsiteSettings',
  redirect: (to) => {
    try {
      return app()?.$rsGlobals.$userHasAccess({ keyType: 'site', key: 'site_management', accessType: 'read', idParam: 'websiteId' })
        ? `/websites/${to.params.websiteId}/settings/global`
        : `/websites/${to.params.websiteId}/settings/forms`;
    } catch (e) {
      console.warn(e);
      return `/websites/${to.params.websiteId}/settings/global`;
    }
  },
  meta: {
    auth,
    dynamicHideFromNav: () => {
      if (app()?.$rsGlobals.$userHasAccess({
        keyType: ModelType.site,
        key: 'pages',
        accessType: 'update',
        idParam: 'websiteId',
      })) {
        return false;
      }

      if (app()?.$rsGlobals.$userHasAccess({
        keyType: ModelType.site,
        key: 'form_settings',
        accessType: 'read',
        idParam: 'websiteId',
      })) {
        return false;
      }

      return true;
    },
    noPagePadding: true,
    title: 'Settings',
    titleKey: 'website.routes.settings',
    icon: 'cogs',
    active: false,
    hideFromNav: false,
    parent: websiteSingle,
  },
};

const websiteGlobalSettings: RouteRecordRaw = {
  name: 'WebsiteGlobalSettings',
  path: '/websites/:websiteId/settings/global',
  meta: {
    auth: siteSettingsAuth,
    title: 'Website Settings',
    titleKey: 'website.routes.websiteSettings',
    icon: 'cogs',
    active: false,
    parent: settings,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
    footerBar: {
      enabled: true,
      // Permission gating handled inside component; static true avoids app() circular init
      hasSave: true,
      hasBottomText: true,
      hasTranslationsBtn: () => !(app()?.$rsGlobals.$siteSourceClientLanguages?.length),
    },
  },
  component: () => import('@/views/websites/single/settings/Global.vue'),
};

const websiteFormSettings: RouteRecordRaw = {
  name: 'WebsiteFormSettings',
  path: '/websites/:websiteId/settings/forms',
  meta: {
    auth: {
      required: true,
      permissions: [{ keyType: 'site', key: 'form_settings', accessType: 'read', idParam: 'websiteId' }],
    },
    title: 'Form Settings',
    titleKey: 'website.routes.formSettings',
    icon: 'cogs',
    active: false,
    parent: settings,
    noPagePadding: true,
  },
  component: () => import('@/views/websites/single/settings/forms/Index.vue'),
};

const websiteFormSettingsAlt: RouteRecordRaw = {
  name: 'WebsiteFormSettingsAlt',
  path: '/websites/:websiteId/forms/settings',
  meta: {
    auth: {
      required: true,
      permissions: [{ keyType: 'site', key: 'form_settings', accessType: 'read', idParam: 'websiteId' }],
    },
    title: 'Settings',
    titleKey: 'website.routes.settings',
    icon: 'cogs',
    active: false,
    parent: settings,
    noPagePadding: true,
    hideFromNav: true,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/form-submissions`,
        title: 'Forms',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
  component: () => import(/* webpackChunkName: "group-website-settings-forms" */ '@/views/websites/single/settings/forms/Index.vue'),
};

const websiteFormSettingsSingle: RouteRecordRaw = {
  name: 'FormDetails',
  path: '/websites/:websiteId/settings/forms/:id/details',
  meta: {
    auth: {
      required: true,
      permissions: [{ keyType: 'site', key: 'form_settings', accessType: 'read', idParam: 'websiteId' }],
    },
    title: 'Form Details',
    titleKey: 'website.routes.formDetails',
    icon: 'cogs',
    active: false,
    noPagePadding: true,
    hidePageTitle: true,
    parent: websiteFormSettings,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasDelete: true,
    },
  },
  component: () => import('@/views/websites/single/settings/forms/single/Index.vue'),
};

const websiteFormSettingsSingleAlt: RouteRecordRaw = {
  name: 'WebsiteFormDetailsAlt',
  path: '/websites/:websiteId/forms/settings/:id/details',
  meta: {
    auth: {
      required: true,
      permissions: [{ keyType: 'site', key: 'form_settings', accessType: 'read', idParam: 'websiteId' }],
    },
    title: 'Details',
    titleKey: 'website.routes.details',
    icon: 'cogs',
    active: false,
    noPagePadding: true,
    hidePageTitle: true,
    parent: websiteFormSettingsAlt,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasDelete: true,
    },
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/form-submissions`,
        title: 'Forms',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/forms/settings`,
        title: 'Settings',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
  component: () => import(/* webpackChunkName: "group-website-settings-forms" */ '@/views/websites/single/settings/forms/single/Index.vue'),
};

const adminSettings: RouteRecordRaw = {
  path: '/websites/:websiteId/superadmin-settings',
  redirect: (to) => `/websites/${to.params.websiteId}/superadmin-settings/global`,
  name: 'WebsiteAdminSettings',
  meta: {
    auth: superadminAuth,
    noPagePadding: true,
    title: 'Superadmin Settings',
    titleKey: 'website.routes.superadminSettings',
    icon: 'cogs',
    active: false,
    hideFromNav: false,
    parent: websiteSingle,
  },
};

const globalSettings: RouteRecordRaw = {
  name: 'GlobalSettings',
  path: '/websites/:websiteId/superadmin-settings/global',
  meta: {
    auth: superadminAuth,
    title: 'Global Settings',
    titleKey: 'website.routes.globalSettings',
    icon: 'cogs',
    active: false,
    parent: adminSettings,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/superadmin-settings/global`,
        title: 'Superadmin Settings',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
  component: () => import('@/views/websites/single/superadmin-settings/Global.vue'),
};

const languageSettings: RouteRecordRaw = {
  name: 'LanguageSettings',
  path: '/websites/:websiteId/superadmin-settings/language',
  meta: {
    auth: superadminAuth,
    title: 'Language Settings',
    titleKey: 'website.routes.languageSettings',
    icon: 'cogs',
    active: false,
    parent: adminSettings,
    dynamicHideFromNav: () => !!app()?.$rsGlobals.$currentClient.parentId,
    footerBar: {
      enabled: true,
      hasSave: true,
    },
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/superadmin-settings/global`,
        title: 'Superadmin Settings',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
  component: () => import('@/views/websites/single/superadmin-settings/Language.vue'),
};

const apiKeySettings: RouteRecordRaw = {
  name: 'APIKeySettings',
  path: '/websites/:websiteId/superadmin-settings/api-keys',
  meta: {
    auth: superadminAuth,
    title: 'APIs & Keys',
    titleKey: 'website.routes.apiKeys',
    icon: 'cogs',
    active: false,
    parent: adminSettings,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/superadmin-settings/global`,
        title: 'Superadmin Settings',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
  component: () => import('@/views/websites/single/superadmin-settings/ApiKey.vue'),
};

const footerScriptsSettings: RouteRecordRaw = {
  name: 'FooterScripts',
  path: '/websites/:websiteId/superadmin-settings/footer-scripts',
  meta: {
    auth: superadminAuth,
    title: 'Footer Scripts',
    titleKey: 'website.routes.footerScripts',
    icon: 'cogs',
    active: false,
    parent: adminSettings,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/superadmin-settings/global`,
        title: 'Superadmin Settings',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
  component: () => import('@/views/websites/single/superadmin-settings/FooterScripts.vue'),
};

const tagScripts: RouteRecordRaw = {
  name: 'TagScripts',
  path: '/websites/:websiteId/superadmin-settings/tag-scripts',
  meta: {
    auth: superadminAuth,
    title: 'Tag Scripts',
    titleKey: 'website.routes.tagScripts',
    icon: 'cogs',
    active: false,
    parent: adminSettings,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/superadmin-settings/global`,
        title: 'Superadmin Settings',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
    footerBar: {
      enabled: true,
      hasSave: true,
    },
  },

  component: () => import('@/views/websites/single/superadmin-settings/TagScripts.vue'),
};

const panelSettings: RouteRecordRaw = {
  name: 'WebsitePanelTemplates',
  component: () => import('@/views/settings/panel-templates/Index.vue'),
  path: '/websites/:websiteId/superadmin-settings/panels',
  meta: {
    auth: superadminAuth,
    title: 'Panel Templates',
    titleKey: 'website.routes.panelTemplates',
    icon: 'cogs',
    active: false,
    noPagePadding: true,
    parent: adminSettings,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/superadmin-settings/global`,
        title: 'Superadmin Settings',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
};

const fieldsBuilder: RouteRecordRaw = {
  path: '/websites/:websiteId/superadmin-settings/unit-list-builder/fields-builder',
  name: 'FieldsBuilder',
  component: () => import('@/views/settings/unit-list-builder/FieldsBuilder.vue'),
  meta: {
    auth: superadminAuth,
    title: 'Fields Builder',
    icon: 'cogs',
    active: false,
    noPagePadding: true,
    parent: adminSettings,
    footerBar: {
      enabled: true,
      hasSave: true,
    },
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/superadmin-settings/global`,
        title: 'Superadmin Settings',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
};

const summaryBuilder: RouteRecordRaw = {
  path: '/websites/:websiteId/superadmin-settings/unit-list-builder/summary-builder',
  name: 'SummaryBuilder',
  component: () => import('@/views/settings/unit-list-builder/SummaryBuilder.vue'),
  meta: {
    auth: superadminAuth,
    title: 'Summary Fields Builder',
    icon: 'cogs',
    active: false,
    noPagePadding: true,
    parent: adminSettings,
    footerBar: {
      enabled: true,
      hasSave: true,
    },
  },
};

const filtersBuilder: RouteRecordRaw = {
  path: '/websites/:websiteId/superadmin-settings/unit-list-builder/filters-builder',
  name: 'FiltersBuilder',
  component: () => import('@/views/settings/unit-list-builder/FiltersBuilder.vue'),
  meta: {
    auth: superadminAuth,
    title: 'Filters Builder',
    icon: 'cogs',
    active: false,
    noPagePadding: true,
    parent: adminSettings,
    footerBar: {
      enabled: true,
      hasSave: true,
    },
  },
};

const sortsBuilder: RouteRecordRaw = {
  path: '/websites/:websiteId/superadmin-settings/unit-list-builder/sorts-builder',
  name: 'SortsBuilder',
  component: () => import('@/views/settings/unit-list-builder/SortsBuilder.vue'),
  meta: {
    auth: superadminAuth,
    title: 'Sorts Builder',
    icon: 'cogs',
    active: false,
    noPagePadding: true,
    parent: adminSettings,
    footerBar: {
      enabled: true,
      hasSave: true,
    },
  },
};

const formsBuilder: RouteRecordRaw = {
  path: '/websites/:websiteId/superadmin-settings/unit-list-builder/forms-builder',
  name: 'FormsBuilder',
  component: () => import('@/views/settings/unit-list-builder/FormsBuilder.vue'),
  meta: {
    auth: superadminAuth,
    title: 'Forms Builder',
    icon: 'cogs',
    active: false,
    noPagePadding: true,
    parent: adminSettings,
    footerBar: {
      enabled: true,
      hasSave: true,
    },
  },
};

const tableSettings: RouteRecordRaw = {
  path: '/websites/:websiteId/superadmin-settings/unit-list-builder/settings',
  name: 'TableSettings',
  component: () => import('@/views/settings/unit-list-builder/Settings.vue'),
  meta: {
    auth: superadminAuth,
    title: 'Settings',
    icon: 'cogs',
    active: false,
    parent: adminSettings,
    footerBar: {
      enabled: true,
      hasSave: true,
    },
  },
};

const panelTemplateSingle: RouteRecordRaw = {
  path: '/websites/:websiteId/superadmin-settings/panels/:id/details',
  name: 'WebsitePanelTemplateSingle',
  component: () => import('@/views/settings/panel-templates/single/Index.vue'),
  meta: {
    auth: superadminAuth,
    title: 'Edit Panel Template',
    noPagePadding: true,
    icon: 'cogs',
    active: false,
    parent: panelSettings,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/superadmin-settings/global`,
        title: 'Superadmin Settings',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/superadmin-settings/panels`,
        title: 'Panel Templates',
      },
      {
        title: 'Details',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
};

// Export the routes to be loaded in index.ts
export default [
  websites,
  websiteSingle,
  menu,
  pages,
  posts,
  postsByType,
  cityPages,
  customFields,
  formSubmissions,
  customFormsList,
  settings,
  adminSettings,
  inquiriesList,
  websiteFormSettingsSingle,
  websiteGlobalSettings,
  websiteFormSettings,
  globalSettings,
  languageSettings,
  apiKeySettings,
  tagScripts,
  footerScriptsSettings,
  panelSettings,
  panelTemplateSingle,
  websiteFormSettingsAlt,
  websiteFormSettingsSingleAlt,
  customFormsAlt,
  customFormsListAlt,
  fieldsBuilder,
  summaryBuilder,
  formsBuilder,
  tableSettings,
  filtersBuilder,
  sortsBuilder,
] as Array<RouteRecordRaw>;
